import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Legend,
} from 'recharts';
import { RotateCcw, Clock3, Users, CalendarDays, Timer, CheckCircle2, Hourglass } from 'lucide-react';
import { StatGrid, ChartCard } from '../../components/dashboard';
import { fetchOTDetailsWithStatus } from '../../store/slices/overtimeSlice';
import { fetchAllEmployees } from '../../store/slices/employeeSlice';
import {
  mapOTRows,
  parseDotNetDate,
  computeOTKpis,
  buildOTByEmployee,
  buildOTByWeekday,
  buildOTByMonth,
  // buildNormalVsOT,   // GetOTDetails has no normal-hours field
  buildOTTypeDonut,
  buildOTHourBuckets,
  buildOTTrend,
  formatOTHours,
} from '../../utils/overtimeAnalytics';

const FIELD =
  'w-full h-9 bg-white border border-gray-200 rounded-lg text-xs px-2.5 outline-none focus:border-primary-500 text-gray-700';
const LABEL = 'text-[11px] font-medium text-gray-500 mb-1 block';

const tooltipStyle = { borderRadius: 8, fontSize: 12, border: '1px solid #e5e7eb' };

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const pad2 = (n) => String(n).padStart(2, '0');

// GetOTDetails needs a month (p_attday=YYYY-MM), so the dashboard always has one.
const currentMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;
};

const round2 = (n) => Math.round(n * 100) / 100;

export default function OTDashboard() {
  const dispatch = useDispatch();
  const { rows, loading, error } = useSelector((s) => s.overtime);
  const { employees: allEmployees = [] } =
    useSelector((s) => s.employees || { employees: [] });

  const [month, setMonth] = useState(currentMonth());

  // Employees are only needed for names (HOT_EMPLOYEE_ID → HED_REPORT_NAME).
  useEffect(() => {
    dispatch(fetchAllEmployees());
  }, [dispatch]);

  useEffect(() => {
    if (month) dispatch(fetchOTDetailsWithStatus({ attDay: month }));
  }, [dispatch, month]);

  const handleReset = () => setMonth(currentMonth());

  // HOT_EMPLOYEE_ID → HED_REPORT_NAME (from Emp/GetEmployees)
  const employeeById = useMemo(() => {
    const map = {};
    (Array.isArray(allEmployees) ? allEmployees : []).forEach((e) => {
      const id = String(e.HED_EMPLOYEE_ID ?? '').trim();
      if (!id) return;
      map[id] = {
        name:
          (e.HED_REPORT_NAME || '').trim() ||
          `${e.HED_FIRST_NAME || ''} ${e.HED_LAST_NAME || ''}`.trim(),
        serviceNo: e.HED_SERVICE_NO || '',
      };
    });
    return map;
  }, [allEmployees]);


  const nameOf = (id) => employeeById[String(id)]?.name || `#${id}`;

  const mapped = useMemo(
    () =>
      mapOTRows(rows).map((r) => {
        const d = parseDotNetDate(r.raw?.HOT_OT_DATE);
        return d
          ? {
              ...r,
              date: `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`,
              weekday: DAY_NAMES[d.getDay()],
            }
          : { ...r, date: '', weekday: '' };
      }),
    [rows]
  );

  const kpis = useMemo(() => computeOTKpis(mapped), [mapped]);
  const typeDonut = useMemo(() => buildOTTypeDonut(mapped), [mapped]);
  // const normalVsOT = useMemo(() => buildNormalVsOT(mapped), [mapped]);
  const byEmployee = useMemo(() => buildOTByEmployee(mapped).slice(0, 8), [mapped]);
  const byWeekday = useMemo(() => buildOTByWeekday(mapped), [mapped]);
  const byMonth = useMemo(() => buildOTByMonth(mapped), [mapped]);
  const buckets = useMemo(() => buildOTHourBuckets(mapped), [mapped]);
  const trend = useMemo(() => buildOTTrend(mapped), [mapped]);

  // Approval split, from HOT_IS_APPROVED
  const approval = useMemo(() => {
    const approved = mapped.filter((r) => r.isApproved);
    const pending = mapped.filter((r) => !r.isApproved);
    const hrs = (list) => round2(list.reduce((s, r) => s + r.otHours, 0));
    return {
      approvedCount: approved.length,
      pendingCount: pending.length,
      approvedHours: hrs(approved),
      pendingHours: hrs(pending),
    };
  }, [mapped]);

  const approvalDonut = [
    { name: 'Approved', value: approval.approvedHours, count: approval.approvedCount, color: '#10b981' },
    { name: 'Pending', value: approval.pendingHours, count: approval.pendingCount, color: '#f59e0b' },
  ].filter((d) => d.count > 0);

  // const otShare = kpis.totalNormalHours + kpis.totalOTHours > 0
  //   ? ((kpis.totalOTHours / (kpis.totalNormalHours + kpis.totalOTHours)) * 100).toFixed(1)
  //   : '0.0';

  // ── KPI cards — this page decides what to show; StatGrid decides how it looks
  const stats = [
    { label: 'Total OT Hours', value: kpis.totalOTHours.toFixed(2), sub: formatOTHours(kpis.totalOTHours), icon: Clock3, tone: 'primary' },
    { label: 'OT Records', value: kpis.records, sub: 'Overtime entries', icon: Timer, tone: 'blue' },
    { label: 'Employees', value: kpis.employees, sub: 'With OT logged', icon: Users, tone: 'violet' },
    { label: 'OT Days', value: kpis.otDays, sub: 'Distinct dates', icon: CalendarDays, tone: 'amber' },
    { label: 'Approved', value: approval.approvedCount, sub: `${approval.approvedHours.toFixed(2)} h`, icon: CheckCircle2, tone: 'emerald' },
    { label: 'Pending', value: approval.pendingCount, sub: `${approval.pendingHours.toFixed(2)} h`, icon: Hourglass, tone: 'indigo' },
    // { label: 'OT Share', value: `${otShare}%`, sub: 'Of total hours worked', icon: TrendingUp, tone: 'emerald' },
    // { label: 'Overnight', value: kpis.overnightCount, sub: 'Crossing midnight', icon: Moon, tone: 'indigo' },
  ];

  const noData = mapped.length === 0;

  return (
    <div className="p-6 space-y-4">
      {/* ── Filters ────────────────────────────────────────────── */}
      <div className="bg-white border border-gray-100 rounded-xl px-4 py-3.5 shadow-sm">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <span className={LABEL}>Month</span>
            <input
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              disabled={loading}
              className={FIELD}
            />
          </div>
          <div className="flex items-end gap-2">
            <button
              onClick={handleReset}
              disabled={loading}
              className="flex items-center gap-2 h-9 px-3 bg-gray-100 text-gray-600 rounded-lg text-xs font-semibold hover:bg-gray-200 transition-colors disabled:opacity-50"
            >
              <RotateCcw size={14} />
              This Month
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>
      )}

      {loading ? (
        <div className="py-24 text-center text-sm text-gray-400">Loading overtime data...</div>
      ) : (
        <>
          {/* ── KPI cards ─────────────────────────────────────── */}
          <StatGrid stats={stats} columns={6} />

          {/* ── Row 1: monthly trend + type donut + approval ─── */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
            <div className="lg:col-span-2">
              <ChartCard title="OT Hours by Month" subtitle="Total overtime logged per calendar month" empty={noData}>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={byMonth} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                    <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#94a3b8' }} />
                    <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} />
                    <Tooltip contentStyle={tooltipStyle} formatter={(v) => [`${v} h`, 'OT Hours']} />
                    <Bar dataKey="hours" fill="#10b981" radius={[4, 4, 0, 0]} maxBarSize={40} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            </div>

            <ChartCard title="OT by Type" subtitle="Share of OT hours" empty={noData}>
              <ResponsiveContainer width="100%" height={190}>
                <PieChart>
                  <Pie data={typeDonut} dataKey="value" nameKey="name" innerRadius={48} outerRadius={74} paddingAngle={2}>
                    {typeDonut.map((e, i) => <Cell key={i} fill={e.color} />)}
                  </Pie>
                  <Tooltip contentStyle={tooltipStyle} formatter={(v, n) => [`${v} h`, n]} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex flex-wrap justify-center gap-x-3 gap-y-1.5 mt-2 text-[11px] text-gray-600">
                {typeDonut.map((d) => (
                  <span key={d.type} className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: d.color }} />
                    {d.name} <span className="font-semibold text-gray-800">{d.value}h</span>
                  </span>
                ))}
              </div>
            </ChartCard>

            <ChartCard title="Approved vs Pending" subtitle="OT hours by approval status" empty={noData}>
              <ResponsiveContainer width="100%" height={190}>
                <PieChart>
                  <Pie data={approvalDonut} dataKey="value" nameKey="name" innerRadius={48} outerRadius={74} paddingAngle={2}>
                    {approvalDonut.map((e, i) => <Cell key={i} fill={e.color} />)}
                  </Pie>
                  <Tooltip contentStyle={tooltipStyle} formatter={(v, n) => [`${v} h`, n]} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex flex-wrap justify-center gap-x-3 gap-y-1.5 mt-2 text-[11px] text-gray-600">
                {approvalDonut.map((d) => (
                  <span key={d.name} className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: d.color }} />
                    {d.name} <span className="font-semibold text-gray-800">{d.value}h</span>
                    <span className="text-gray-400">({d.count})</span>
                  </span>
                ))}
              </div>
            </ChartCard>

            {/* GetOTDetails has no normal-hours field, so this card is hidden.
            <ChartCard title="Normal vs OT Hours" subtitle={`Overtime is ${otShare}% of total`} empty={noData}>
              <ResponsiveContainer width="100%" height={190}>
                <PieChart>
                  <Pie data={normalVsOT} dataKey="value" nameKey="name" innerRadius={48} outerRadius={74} paddingAngle={2}>
                    {normalVsOT.map((e, i) => <Cell key={i} fill={e.color} />)}
                  </Pie>
                  <Tooltip contentStyle={tooltipStyle} formatter={(v, n) => [`${v} h`, n]} />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>
            */}
          </div>

          {/* ── Row 2: top employees + weekday + buckets ────────── */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <ChartCard title="Top Employees by OT Hours" subtitle="Highest overtime first" empty={noData}>
              <div className="space-y-3.5">
                {byEmployee.map((e, i) => {
                  const max = Math.max(...byEmployee.map((x) => x.hours), 1);
                  const isTop = i < 3;
                  const name = nameOf(e.employeeId);
                  return (
                    <div key={e.employeeId} className="flex items-center gap-3">
                      <span
                        className={`w-6 h-6 shrink-0 rounded-full text-white text-[11px] font-bold flex items-center justify-center ${
                          isTop ? 'bg-primary-500' : 'bg-gray-300'
                        }`}
                      >
                        {i + 1}
                      </span>
                      <span
                        className={`w-32 shrink-0 text-xs truncate ${
                          isTop ? 'text-primary-600 font-medium' : 'text-gray-600'
                        }`}
                        title={`${name} (#${e.employeeId})`}
                      >
                        {name}
                      </span>
                      <div className="flex-1 h-1.5 bg-gray-100 rounded-full relative">
                        <div
                          className="absolute inset-y-0 left-0 bg-primary-500 rounded-full"
                          style={{ width: `${(e.hours / max) * 100}%` }}
                        />
                      </div>
                      <span className="w-14 text-right text-xs font-semibold text-gray-700 tabular-nums">
                        {e.hours.toFixed(2)}h
                      </span>
                    </div>
                  );
                })}
              </div>
            </ChartCard>

            <ChartCard title="OT by Day of Week" subtitle="Which days overtime concentrates on" empty={noData}>
              <ResponsiveContainer width="100%" height={230}>
                <BarChart data={byWeekday} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                  <XAxis dataKey="short" tick={{ fontSize: 10, fill: '#94a3b8' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v) => [`${v} h`, 'OT Hours']} />
                  <Bar dataKey="hours" fill="#f59e0b" radius={[4, 4, 0, 0]} maxBarSize={30} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="OT Length Distribution" subtitle="How long each OT stint runs" empty={noData}>
              <ResponsiveContainer width="100%" height={230}>
                <BarChart data={buckets} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                  <XAxis dataKey="label" tick={{ fontSize: 9, fill: '#94a3b8' }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#94a3b8' }} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v) => [v, 'Records']} />
                  <Bar dataKey="count" fill="#3b82f6" radius={[4, 4, 0, 0]} maxBarSize={34} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          {/* ── Row 3: daily trend ─────────────────────────────── */}
          <ChartCard title="Daily OT Trend" subtitle="OT hours per OT date" empty={noData}>
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={trend} margin={{ top: 8, right: 16, left: -18, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#94a3b8' }} />
                <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} />
                <Tooltip
                  contentStyle={tooltipStyle}
                  formatter={(v) => [`${v} h`, 'OT Hours']}
                  labelFormatter={(label, payload) => payload?.[0]?.payload?.date || label}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="hours" name="OT Hours" stroke="#10b981" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
        </>
      )}
    </div>
  );
}